/**
 * Clear OCR-Derived Data Action
 *
 * Resets all OCR-populated fields when the patient skips ID photo capture
 * or selects "I don't have an ID". Always preserves dateOfBirth — if OCR
 * previously overwrote it, restores the pre-OCR value; otherwise keeps
 * the existing value (e.g. from returning patient check).
 *
 * The skip/show decision for dobInput is handled by the _skippedIdCapture
 * flag (set on idTypeSelection SUBMIT), not by this action.
 */

import { assign } from "xstate";
import { clearAddressResponses } from "../helper/address-response-mapper";
import type { ActionWithMetadata } from "./save-actor-result.action";

export const clearOcrDerivedData: ActionWithMetadata = {
  // biome-ignore lint/suspicious/noExplicitAny: XState action params are complex
  impl: assign(({ context }: any) => {
    // If OCR ran (ocrData exists), restore the pre-OCR DOB (may be undefined
    // if patient had no DOB before scanning). If OCR never ran, keep the
    // current dateOfBirth as-is (e.g. from returning patient check).
    const dateOfBirth = context.ocrData
      ? context._preOcrDateOfBirth
      : context.dateOfBirth;

    return {
      ocrData: undefined,
      firstName: undefined,
      lastName: undefined,
      dateOfBirth,
      sex: undefined,
      idType: "noId",
      hasPhysicalId: false,
      _preOcrDateOfBirth: undefined,
      licensePhotoFront: undefined,
      licensePhotoBack: undefined,
      passportPhoto: undefined,
      responses: clearAddressResponses(
        context.intakeFormSections,
        context.responses || {},
      ),
    };
  }),
  metadata: {
    displayName: "clearOcrDerivedData",
    description: "Clear all OCR-derived identity and address data from context",
    category: "context",
  },
};
